import { useMemo, useRef, useState, useEffect } from "react";
import { PlanFeatureCollection } from "@/src/api/Plan";
import { isZoneSymbol, PLAN_ZONES, PLAN_AREAS, PlanZoneSymbol, PlanAreaSymbol } from "@/src/lib/planStyles";

/**
 * Autocomplete wyszukiwania strefy po `oznaczenie` (np. "34SK", "1OUZ").
 *
 * Używane w dwóch miejscach:
 *  - `PlanAdminPanel` (podgląd admina) — mapa statyczna, fit() na feature
 *  - `TenantPublicPage` (publiczna mapa wniosków) — główny OlMap, fit + popup
 *
 * Jedno wspólne API: `data` (features już załadowane przez useGetPlanFeaturesQuery)
 * + `onSelect(feature)` — rodzic decyduje co robi z wybranym feature'em
 * (zoom na extent, podświetlenie, popup).
 *
 * UX:
 *  - Input z placeholderem "Wyszukaj strefę (np. 34SK)"
 *  - Po wpisaniu ≥1 znaku pokazuje listę pasujących (max 12)
 *  - Filter: oznaczenie.toLowerCase().includes(query.toLowerCase())
 *  - Sugestia pokazuje kolorowy kwadracik + oznaczenie + symbol + nazwa
 *  - Klik lub Enter wybiera pierwszą sugestię + czyści input
 *  - Klik poza komponentem chowa listę
 */

export type PlanSearchBoxFeature = PlanFeatureCollection["features"][number];

export type PlanSearchBoxProps = {
  data: PlanFeatureCollection | null | undefined;
  /** `query` — wpisany tekst, z którego wybrano strefę. */
  onSelect: (feature: PlanSearchBoxFeature, query: string) => void;
  /** Enter przy braku pasujących stref (np. rejestr wyszukiwań konsultanta). */
  onSearchMiss?: (query: string) => void;
  placeholder?: string;
  /** Maksymalna liczba sugestii. Default 12. */
  maxSuggestions?: number;
};

function PlanSearchBox({
  data,
  onSelect,
  onSearchMiss,
  placeholder = "Wyszukaj strefę po oznaczeniu (np. 34SK)",
  maxSuggestions = 12,
}: PlanSearchBoxProps) {
  const [query, setQuery] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [highlightedIdx, setHighlightedIdx] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);

  // Filter: oznaczenie zaczyna się od query (prefix match). Nie pokazujemy
  // pełnego `includes`, bo dla jednoznakowego query ("1") lista byłaby bardzo
  // długa i myląca — user chce zobaczyć tylko te tereny, które zaczynają się
  // od wpisanego znaku. Zachowujemy preferencję `equals > startsWith` przy
  // sortowaniu (dokładne dopasowanie na górze).
  const suggestions = useMemo(() => {
    if (!data || query.trim().length === 0) return [];
    const q = query.trim().toLowerCase();
    const matches: Array<{ feature: PlanSearchBoxFeature; score: number }> = [];
    for (const f of data.features) {
      const oz = (f.properties.oznaczenie ?? "").toLowerCase();
      if (!oz) continue;
      if (oz === q) matches.push({ feature: f, score: 0 });
      else if (oz.startsWith(q)) matches.push({ feature: f, score: 1 });
      // Pominięte: `oz.includes(q)` — user chce tylko prefix match
      if (matches.length >= maxSuggestions * 3) break; // limit pracy
    }
    matches.sort((a, b) => {
      if (a.score !== b.score) return a.score - b.score;
      // Tie-breaker: alfabetycznie po oznaczeniu
      return (a.feature.properties.oznaczenie ?? "").localeCompare(
        b.feature.properties.oznaczenie ?? "",
      );
    });
    return matches.slice(0, maxSuggestions).map((m) => m.feature);
  }, [data, query, maxSuggestions]);

  // Zamknij listę po kliknięciu poza komponentem.
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: MouseEvent) => {
      if (!containerRef.current?.contains(e.target as Node)) setIsOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [isOpen]);

  // Reset highlight gdy się zmienia lista.
  useEffect(() => {
    setHighlightedIdx(0);
  }, [suggestions.length]);

  const handleSelect = (feature: PlanSearchBoxFeature) => {
    onSelect(feature, query.trim());
    setQuery("");
    setIsOpen(false);
  };

  const handleKey = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && suggestions.length === 0 && query.trim()) {
      onSearchMiss?.(query.trim());
      return;
    }
    if (!isOpen || suggestions.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlightedIdx((i) => Math.min(i + 1, suggestions.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlightedIdx((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      handleSelect(suggestions[highlightedIdx]);
    } else if (e.key === "Escape") {
      setIsOpen(false);
    }
  };

  return (
    <div ref={containerRef} style={{ position: "relative", width: "100%" }}>
      <input
        type="text"
        value={query}
        onChange={(e) => {
          setQuery(e.target.value);
          setIsOpen(true);
        }}
        onFocus={() => query.length > 0 && setIsOpen(true)}
        onKeyDown={handleKey}
        placeholder={placeholder}
        style={inputStyle}
      />
      {isOpen && suggestions.length > 0 && (
        <ul style={listStyle}>
          {suggestions.map((f, idx) => (
            <SuggestionRow
              key={f.id}
              feature={f}
              highlighted={idx === highlightedIdx}
              onClick={() => handleSelect(f)}
              onMouseEnter={() => setHighlightedIdx(idx)}
            />
          ))}
        </ul>
      )}
      {isOpen && query.length > 0 && suggestions.length === 0 && (
        <div style={{ ...listStyle, padding: "8px 12px", color: "#6b7280", fontSize: 13 }}>
          Brak stref pasujących do &bdquo;{query}&rdquo;
        </div>
      )}
    </div>
  );
}

function SuggestionRow({
  feature,
  highlighted,
  onClick,
  onMouseEnter,
}: {
  feature: PlanSearchBoxFeature;
  highlighted: boolean;
  onClick: () => void;
  onMouseEnter: () => void;
}) {
  const p = feature.properties;
  const sym = p.symbol;
  const zoneStyle = isZoneSymbol(sym) ? PLAN_ZONES[sym as PlanZoneSymbol] : null;
  const areaStyle = sym in PLAN_AREAS ? PLAN_AREAS[sym as PlanAreaSymbol] : null;
  return (
    <li
      onMouseDown={(e) => {
        // onMouseDown zamiast onClick — w onClick zdążyłby zamknąć listę
        // przez handler mousedown document (powyżej), zanim dotarłoby
        // do nas `click`.
        e.preventDefault();
        onClick();
      }}
      onMouseEnter={onMouseEnter}
      style={{
        ...itemStyle,
        background: highlighted ? "#eff6ff" : "transparent",
        cursor: "pointer",
      }}>
      {/* Color swatch */}
      {zoneStyle && (
        <span
          style={{
            display: "inline-block",
            width: 16,
            height: 16,
            background: zoneStyle.fill,
            border: `1px solid ${zoneStyle.stroke}`,
            flexShrink: 0,
            borderRadius: 2,
          }}
        />
      )}
      {areaStyle && (
        <span
          style={{
            display: "inline-block",
            width: 16,
            height: 16,
            border: `2px dashed ${areaStyle.stroke}`,
            flexShrink: 0,
            borderRadius: 2,
          }}
        />
      )}
      <span style={{ fontFamily: "monospace", fontWeight: 600, minWidth: 60 }}>
        {p.oznaczenie || "—"}
      </span>
      <span style={{ fontFamily: "monospace", color: "#6b7280", minWidth: 40 }}>{sym}</span>
      <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
        {zoneStyle?.name || areaStyle?.name || p.nazwaLabel || ""}
      </span>
    </li>
  );
}

const inputStyle: React.CSSProperties = {
  width: "100%",
  padding: "8px 12px",
  fontSize: 14,
  border: "1px solid #d1d5db",
  borderRadius: 6,
  background: "#fff",
  fontFamily: "system-ui, -apple-system, sans-serif",
};

const listStyle: React.CSSProperties = {
  position: "absolute",
  top: "calc(100% + 4px)",
  left: 0,
  right: 0,
  margin: 0,
  padding: 0,
  listStyle: "none",
  background: "#fff",
  border: "1px solid #d1d5db",
  borderRadius: 6,
  boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
  maxHeight: 400,
  overflow: "auto",
  zIndex: 1100,
};

const itemStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 8,
  padding: "6px 10px",
  fontSize: 13,
  borderBottom: "1px solid #f3f4f6",
};

export default PlanSearchBox;
